import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import MobileStepper from '@mui/material/MobileStepper';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import ImageCaptcha from './ImageCaptcha';

// Import der Icons
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FeedbackIcon from '@mui/icons-material/Feedback';

const AnimatedCar = ({ image, initialDelay }) => {
    const { t } = useLanguage();
    const [raceSpeed, setRaceSpeed] = useState('10s');

    useEffect(() => {
        const randomDuration = 4 + Math.random() * 4;
        setRaceSpeed(`${randomDuration.toFixed(2)}s`);
    }, []);

    return (
        <Box
            component="img"
            src={image}
            alt={t('betaForm_alt_carreraCar')}
            sx={{
                position: 'absolute',
                top: 0,
                height: '30px',
                animationName: 'raceAcross',
                animationTimingFunction: 'linear',
                animationIterationCount: 'infinite',
                animationDuration: raceSpeed,
                animationDelay: initialDelay,
            }}
        />
    );
};

const BetaForm = ({ onSuccess }) => {
    const { t } = useLanguage();
    const theme = useTheme();

    // HIER DIE UMWELTVARIABLE ABFRAGEN
    // Wenn in AWS 'true' gesetzt ist, wird die Anmeldung blockiert
    const isWaitlistClosed = true;

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', birthYear: '',
        country: '',
        hybridUsername: '',
        operatingSystem: '', device: '', carreraClubMember: '', carreraUsageDuration: '',
        carreraSystems: { digital: false, evolution: false, go: false, first: false, none: false },
        hybridExperience: '', privacy: false, agreement: false, marketingConsent: false
    });

    const [isWizardActive, setIsWizardActive] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [stepValidation, setStepValidation] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [captchaResetKey, setCaptchaResetKey] = useState(0);

    const steps = [
        t('betaForm_section_personal'),
        t('betaForm_section_technical'),
        t('betaForm_section_profile'),
        t('betaForm_section_legal'),
    ];
    const maxSteps = steps.length;

    useEffect(() => {
        switch (activeStep) {
            case 0:
                setStepValidation(
                    !!formData.firstName && !!formData.lastName && !!formData.email &&
                    !!formData.birthYear && !!formData.country && formData.country !== 'other' &&
                    !!formData.hybridUsername
                );
                break;
            case 1:
                setStepValidation(
                    !!formData.operatingSystem &&
                    formData.operatingSystem !== 'other' &&
                    !!formData.device
                );
                break;
            case 2:
                const atLeastOneSystemSelected = Object.values(formData.carreraSystems).some(v => v);
                setStepValidation(
                    !!formData.carreraClubMember && !!formData.carreraUsageDuration &&
                    !!formData.hybridExperience && atLeastOneSystemSelected
                );
                break;
            case 3:
                setStepValidation(true);
                break;
            default:
                setStepValidation(false);
        }
    }, [formData, activeStep]);

    useEffect(() => {
        const requiredFields = [
            'firstName', 'lastName', 'email', 'birthYear', 'country', 'hybridUsername',
            'operatingSystem', 'device', 'carreraClubMember', 'carreraUsageDuration',
            'hybridExperience'
        ];
        const allTextFieldsFilled = requiredFields.every(field => formData[field] && String(formData[field]).trim() !== '');
        const allCheckboxesChecked = formData.privacy && formData.agreement && formData.marketingConsent;
        const atLeastOneSystemSelected = Object.values(formData.carreraSystems).some(value => value === true);

        setIsFormValid(
            allTextFieldsFilled && allCheckboxesChecked &&
            atLeastOneSystemSelected &&
            formData.operatingSystem !== 'other' &&
            formData.country !== 'other' &&
            isCaptchaVerified
        );
    }, [formData, isCaptchaVerified]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prevState => ({ ...prevState, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSystemChange = (event) => {
        const { name, checked } = event.target;
        setFormData(prevState => ({
            ...prevState,
            carreraSystems: {
                ...prevState.carreraSystems,
                [name]: checked
            }
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!isFormValid) return;
        setIsSubmitting(true);
        setError(null);

        const middlewareUrl = '/api/beta-signup';
        try {
            const response = await fetch(middlewareUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
            if (!response.ok) throw new Error('Network response was not ok.');
            onSuccess();
        } catch (err) {
            console.error('Error submitting form:', err);
            setError(t('betaForm_error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCaptchaVerify = (isCorrect) => {
        setIsCaptchaVerified(isCorrect);
        if (!isCorrect) {
            setTimeout(() => {
                setCaptchaResetKey(prevKey => prevKey + 1);
            }, 1500);
        }
    };

    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const isSubmitDisabled = !isFormValid || isSubmitting;
    const isNextButtonDisabled = !stepValidation;

    const privacyLabel = (<span>{t('betaForm_privacy_part1')}<Link href="/datenschutz" target="_blank" sx={{ verticalAlign: 'baseline' }}>{t('betaForm_privacy_link')}</Link>{t('betaForm_privacy_part2')}</span>);
    const agreementLabel = (<span>{t('betaForm_agreement_part1')}<Link href="/beta-tester-agreement" target="_blank" sx={{ verticalAlign: 'baseline' }}>{t('betaForm_agreement_link')}</Link>{t('betaForm_agreement_part2')}</span>);

    const currentYear = new Date().getFullYear();
    const maxYear = currentYear - 18;
    const minYear = currentYear - 100;
    const years = [];
    for (let i = maxYear; i >= minYear; i--) {
        years.push(i);
    }

    const showTitle = !isWizardActive || (isWizardActive && !isWaitlistClosed);

    return (
        <>
            <Box sx={{ position: 'relative', width: '110%', height: '40px', overflow: 'hidden', mb: 1 }}>
                <AnimatedCar image="img/cars/BMW_M4_Carrera.png" initialDelay="0s" />
                <AnimatedCar image="img/cars/Ford_Mustang_Champion_Spirit_64.png" initialDelay="-4.5s" />
            </Box>

            {showTitle && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h5" component="h1">
                        {t('betaForm_title')}
                    </Typography>
                </Box>
            )}

            {!isWizardActive && (
                <>
                    <Box sx={{ width: '100%', mb: 3, mt: 1 }}>
                        <List>
                            <ListItem><ListItemIcon><HourglassTopIcon color="primary" /></ListItemIcon><ListItemText primary={t('betaForm_waitlist_title')} secondary={t('betaForm_waitlist_desc')} /></ListItem>
                            <ListItem><ListItemIcon><WarningAmberIcon color="primary" /></ListItemIcon><ListItemText primary={t('betaForm_risk_title')} secondary={t('betaForm_risk_desc')} /></ListItem>
                            <ListItem><ListItemIcon><FeedbackIcon color="primary" /></ListItemIcon><ListItemText primary={t('betaForm_feedback_title')} secondary={t('betaForm_feedback_desc')} /></ListItem>
                        </List>
                    </Box>

                    <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{
                            mt: 3, mb: 2, fontSize: '1rem',
                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.01)',
                                boxShadow: theme.shadows[4],
                            }
                        }}
                        onClick={() => setIsWizardActive(true)}
                    >
                        {t('betaForm_join_waitlist')}
                    </Button>
                </>
            )}

            {/* NEUE LOGIK: Wenn Button geklickt wurde UND Warteliste geschlossen ist */}
            {isWizardActive && isWaitlistClosed && (
                <Box sx={{ mt: 4, textAlign: 'center', px: 3 }}>

                    <img
                        src="/img/close.png"
                        alt="Carrera Logo"
                        style={{ height: '75px', marginTop: 1, marginBottom: 10 }}
                    />

                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        Registrierung vorübergehend geschlossen
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                        Aufgrund der außerordentlich hohen Nachfrage haben wir die Aufnahme in die Warteliste momentan pausiert.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                        Herzlichen Dank für das überwältigende Interesse!
                    </Typography>





                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.6, maxWidth: '600px', mx: 'auto' }}>
                        Wir erfassen und prüfen jede eingehende Rückmeldung sorgfältig. Bitte haben Sie jedoch Verständnis, dass wir aufgrund der Vielzahl an Nachrichten nicht persönlich antworten können. Ihr Feedback fließt stattdessen direkt in die kontinuierliche Optimierung und Weiterentwicklung ein.
                    </Typography>


                    <Box sx={{ my: 3, p: 2, bgcolor: 'action.hover', borderRadius: 4, border: '1px dashed', borderColor: 'text.disabled' }}>
                        <img style={{height: 150}} src="/img/captain-carrera.gif" alt="Captain Carrera waving a yellow flag"/>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                            🏁 Safety Car Phase!
                        </Typography>
                        <Typography variant="body2">
                            Das Rennen um die Beta-Plätze ist gerade neutralisiert.
                            <br />
                            Nutzen Sie die Gelbphase für einen taktischen Boxenstopp im <Link href="https://carrera-toys.com" target="_blank" underline="hover" sx={{ fontWeight: 'bold' }}>Online-Shop</Link>.
                            Mehr Gegner, nie dagewesene Strecken – machen Sie Ihr Setup startklar für NPCs und Arcade Mode!
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* ALTE LOGIK: Wizard wird nur angezeigt, wenn Warteliste NICHT geschlossen ist */}
            {isWizardActive && !isWaitlistClosed && (
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>

                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        {steps[activeStep]}
                    </Typography>

                    <AnimatePresence mode="wait">
                        {activeStep === 0 && (
                            <motion.div
                                key="step0"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                <TextField margin="normal" required fullWidth id="firstName" label={t('betaForm_firstName')} name="firstName" value={formData.firstName} onChange={handleChange} />
                                <TextField margin="normal" required fullWidth id="lastName" label={t('betaForm_lastName')} name="lastName" value={formData.lastName} onChange={handleChange} />
                                <TextField margin="normal" required fullWidth id="email" label={t('betaForm_email')} name="email" type="email" value={formData.email} onChange={handleChange} />
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel id="birthyear-select-label">{t('betaForm_birthYear')}</InputLabel>
                                    <Select
                                        labelId="birthyear-select-label"
                                        id="birthYear"
                                        name="birthYear"
                                        value={formData.birthYear}
                                        label={t('betaForm_birthYear')}
                                        onChange={handleChange}
                                    >
                                        {years.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth margin="normal" required error={formData.country === 'other'}>
                                    <InputLabel id="country-select-label">{t('betaForm_country') || "Land"}</InputLabel>
                                    <Select
                                        labelId="country-select-label"
                                        id="country"
                                        name="country"
                                        value={formData.country}
                                        label={t('betaForm_country') || "Land"}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="de">Deutschland</MenuItem>
                                        <MenuItem value="at">Österreich</MenuItem>
                                        <MenuItem value="other">Andere</MenuItem>
                                    </Select>
                                    {formData.country === 'other' && (
                                        <FormHelperText>
                                            {t('betaForm_country_error') || "Aus rechtlichen Gründen ist das Beta-Programm derzeit nur in Deutschland und Österreich verfügbar."}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                <TextField margin="normal" required fullWidth id="hybridUsername" label={t('betaForm_username')} name="hybridUsername" value={formData.hybridUsername} onChange={handleChange} />
                            </motion.div>
                        )}
                        {activeStep === 1 && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                <FormControl fullWidth margin="normal" required error={formData.operatingSystem === 'other'}>
                                    <InputLabel id="os-select-label">{t('betaForm_os')}</InputLabel>
                                    <Select labelId="os-select-label" id="operatingSystem" name="operatingSystem" value={formData.operatingSystem} label={t('betaForm_os')} onChange={handleChange}>
                                        <MenuItem value="ios">iOS</MenuItem>
                                        <MenuItem value="android">Android</MenuItem>
                                        <MenuItem value="other">{t('betaForm_os_other')}</MenuItem>
                                    </Select>
                                    {formData.operatingSystem === 'other' && (<FormHelperText>{t('betaForm_os_unsupported')}</FormHelperText>)}
                                </FormControl>
                                <TextField margin="normal" required fullWidth id="device" label={t('betaForm_device')} name="device" value={formData.device} onChange={handleChange} />
                            </motion.div>
                        )}
                        {activeStep === 2 && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 1.5, sm: 2 }, mt: 1, mb: 2, backgroundColor: 'action.hover' }}>
                                    <FormControl component="fieldset" margin="normal" required fullWidth>
                                        <FormLabel component="legend">{t('betaForm_clubMember')}</FormLabel>
                                        <RadioGroup row name="carreraClubMember" value={formData.carreraClubMember} onChange={handleChange}>
                                            <FormControlLabel value="yes" control={<Radio />} label={t('betaForm_yes')} />
                                            <FormControlLabel value="no" control={<Radio />} label={t('betaForm_no')} />
                                        </RadioGroup>
                                    </FormControl>
                                    <FormControl fullWidth margin="normal" required>
                                        <InputLabel id="usage-duration-label">{t('betaForm_usageDuration')}</InputLabel>
                                        <Select labelId="usage-duration-label" id="carreraUsageDuration" name="carreraUsageDuration" value={formData.carreraUsageDuration} label={t('betaForm_usageDuration')} onChange={handleChange}>
                                            <MenuItem value="lt1">{t('betaForm_usage_lt1')}</MenuItem>
                                            <MenuItem value="1to5">{t('betaForm_usage_1to5')}</MenuItem>
                                            <MenuItem value="gt5">{t('betaForm_usage_gt5')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl component="fieldset" margin="normal" required fullWidth>
                                        <FormLabel component="legend">{t('betaForm_systems')}</FormLabel>
                                        <FormHelperText>{t('betaForm_systems_helper')}</FormHelperText>
                                        <FormGroup>
                                            <FormControlLabel control={<Checkbox checked={formData.carreraSystems.digital} onChange={handleSystemChange} name="digital" />} label="Digital 124 / 132" />
                                            <FormControlLabel control={<Checkbox checked={formData.carreraSystems.evolution} onChange={handleSystemChange} name="evolution" />} label="Evolution" />
                                            <FormControlLabel control={<Checkbox checked={formData.carreraSystems.go} onChange={handleSystemChange} name="go" />} label="GO!!!" />
                                            <FormControlLabel control={<Checkbox checked={formData.carreraSystems.first} onChange={handleSystemChange} name="first" />} label="Carrera FIRST" />
                                            <FormControlLabel control={<Checkbox checked={formData.carreraSystems.none} onChange={handleSystemChange} name="none" />} label={t('betaForm_systems_none')} />
                                        </FormGroup>
                                    </FormControl>
                                    <FormControl component="fieldset" margin="normal" required fullWidth>
                                        <FormLabel component="legend">{t('betaForm_hybridExperience')}</FormLabel>
                                        <RadioGroup row name="hybridExperience" value={formData.hybridExperience} onChange={handleChange}>
                                            <FormControlLabel value="yes" control={<Radio />} label={t('betaForm_yes')} />
                                            <FormControlLabel value="no" control={<Radio />} label={t('betaForm_no')} />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            </motion.div>
                        )}
                        {activeStep === 3 && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                <FormGroup sx={{ mt: 1 }}>
                                    <FormControlLabel control={<Checkbox required color="primary" name="privacy" checked={formData.privacy} onChange={handleChange} />} label={privacyLabel} />
                                    <FormControlLabel control={<Checkbox required color="primary" name="agreement" checked={formData.agreement} onChange={handleChange} />} label={agreementLabel} />
                                    <FormControlLabel control={<Checkbox required color="primary" name="marketingConsent" checked={formData.marketingConsent} onChange={handleChange} />} label={t('betaForm_marketingConsent')} />
                                </FormGroup>
                                <Box sx={{ mt: 3 }}>
                                    <ImageCaptcha onVerify={handleCaptchaVerify} resetTrigger={captchaResetKey} />
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {error && (<Typography color="error" align="center" sx={{ mt: 2, mb: 1 }}>{error}</Typography>)}

                    <MobileStepper
                        variant="dots"
                        steps={maxSteps}
                        position="static"
                        activeStep={activeStep}
                        sx={{
                            flexGrow: 1, mt: 3, backgroundColor: 'transparent',
                            '& .MuiMobileStepper-dotActive': {
                                animation: 'pulse 1.5s infinite ease-in-out',
                            },
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)', boxShadow: 'none' },
                                '50%': { transform: 'scale(1.2)', boxShadow: '0 0 0 4px rgba(0, 123, 255, 0.4)' },
                                '100%': { transform: 'scale(1)', boxShadow: 'none' },
                            },
                        }}
                        nextButton={
                            activeStep === maxSteps - 1 ? (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitDisabled}
                                    sx={{
                                        fontSize: '0.9rem',
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            boxShadow: theme.shadows[4],
                                        }
                                    }}
                                >
                                    {isSubmitting ? t('betaForm_submitting') : t('betaForm_submit')}
                                </Button>
                            ) : (
                                <Button
                                    size="small"
                                    onClick={handleNext}
                                    disabled={isNextButtonDisabled}
                                    sx={{
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            boxShadow: theme.shadows[2],
                                        }
                                    }}
                                >
                                    {t('betaForm_next')}
                                    {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                                </Button>
                            )
                        }
                        backButton={
                            <Button
                                size="small"
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                sx={{
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                        boxShadow: theme.shadows[2],
                                    }
                                }}
                            >
                                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                                {t('betaForm_back')}
                            </Button>
                        }
                    />
                </Box>
            )}
        </>
    );
};

export default BetaForm;